import React, { useEffect, useState } from "react";
import { API, get } from "modules/api";
import Client, { ClientType } from "modules/parties/types/Client";
import Questionnaire, {
  QuestionsMap,
} from "modules/workflow/types/Questionnaire";
import { AnswersMap } from "modules/workflow/types/Answer";
import { MedicaidLeadStatus } from "modules/medicaid/types";
import AnimatedLoadingOverlay from "components/AnimatedLoadingOverlay";
import { AnimatedLoadingOverlaySize } from "components/AnimatedLoadingOverlay/types";
import PublicMedicaidLeadInterview, {
  PublicCompleteData,
} from "./PublicMedicaidLeadInterview";
import styles from "./styles.module.scss";

type LeadPayload = {
  questionnaire: Questionnaire;
  questions: QuestionsMap;
  clients?: Record<string, any>;
  answers?: AnswersMap;
  pageIdx?: number;
  status?: MedicaidLeadStatus;
};

type LoadState =
  | { status: "loading" }
  | {
      status: "ready";
      questionnaire: Questionnaire;
      questions: QuestionsMap;
      clients: Record<string, Client>;
      answers: AnswersMap;
      pageIdx: number;
    }
  | { status: "error" };

type Props = {
  publicLeadId: string;
  jurisdiction: string;
  onComplete: (data: PublicCompleteData) => void;
  onError: () => void;
};

const normalizeClients = (
  raw: Record<string, any> | undefined,
): Record<string, Client> => {
  const clients: Record<string, Client> = {};
  const main = raw?.[ClientType.MAIN] ?? raw?.["Main"] ?? raw?.["main"];
  const spouse = raw?.[ClientType.SPOUSE] ?? raw?.["Spouse"] ?? raw?.["spouse"];
  if (main) clients[ClientType.MAIN] = main as Client;
  if (spouse) clients[ClientType.SPOUSE] = spouse as Client;
  return clients;
};

const PublicMedicaidInterviewStep: React.FC<Props> = ({
  publicLeadId,
  jurisdiction,
  onComplete,
  onError,
}) => {
  const [loadState, setLoadState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let mounted = true;
    get<LeadPayload>(API.PUBLIC_MEDICAID_LEAD({ publicLeadId }))
      .then(({ data }) => {
        if (!mounted) return;

        setLoadState({
          status: "ready",
          questionnaire: data.questionnaire,
          questions: data.questions,
          clients: normalizeClients(data.clients),
          answers: data.answers ?? {},
          pageIdx: data.pageIdx ?? 0,
        });
      })
      .catch(() => {
        if (!mounted) return;
        setLoadState({ status: "error" });
        onError();
      });
    return () => {
      mounted = false;
    };
  }, [publicLeadId, onError]);

  if (loadState.status === "loading") {
    return (
      <div className={styles["container--centered"]}>
        <AnimatedLoadingOverlay size={AnimatedLoadingOverlaySize.LARGE} />
      </div>
    );
  }

  if (loadState.status === "error") {
    return null;
  }

  return (
    <PublicMedicaidLeadInterview
      publicLeadId={publicLeadId}
      questionnaire={loadState.questionnaire}
      questions={loadState.questions}
      initialClients={loadState.clients}
      initialAnswers={loadState.answers}
      initialPageIdx={loadState.pageIdx}
      jurisdiction={jurisdiction}
      onComplete={onComplete}
    />
  );
};

export default PublicMedicaidInterviewStep;
